import { useRef, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createImageData } from './verificationUtils';

/**
 * WebcamCapture - Camera capture component that manually manages
 * getUserMedia for reliable rear-camera support on mobile.
 *
 * @param {Object} props
 * @param {Function} props.onCapture - Callback when image is captured
 * @param {Function} props.onError - Callback when error occurs
 * @param {boolean} props.mirrored - Whether to mirror the preview
 * @param {string} props.facingMode - 'user' (front) or 'environment' (rear)
 * @param {string} props.className - Additional CSS classes
 */
function WebcamCapture({
  onCapture,
  onError,
  mirrored = false,
  facingMode = 'user',
  className = ''
}) {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [hasPermission, setHasPermission] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);

  // Start camera stream with fallback logic
  useEffect(() => {
    let cancelled = false;

    const startCamera = async () => {
      // Stop any existing stream first
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      setHasPermission(null);

      const tryConstraints = async (constraints) => {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return null;
        }
        return stream;
      };

      try {
        let stream = null;

        if (facingMode === 'environment') {
          // Try exact environment first, then loose, then any camera
          try {
            stream = await tryConstraints({
              video: { facingMode: { exact: 'environment' }, width: 1280, height: 720 },
              audio: false
            });
          } catch (_e1) {
            try {
              stream = await tryConstraints({
                video: { facingMode: 'environment', width: 1280, height: 720 },
                audio: false
              });
            } catch (_e2) {
              stream = await tryConstraints({
                video: { width: 1280, height: 720 },
                audio: false
              });
            }
          }
        } else {
          stream = await tryConstraints({
            video: { facingMode: facingMode, width: 1280, height: 720 },
            audio: false
          });
        }

        if (!stream || cancelled) return;

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setHasPermission(true);
      } catch (error) {
        if (cancelled) return;
        console.error('Camera access error:', error);
        setHasPermission(false);
        if (onError) {
          const permissionError = new Error(
            t('verification.camera-permission-denied', {
              defaultValue:
                'Camera permission denied. Please enable camera access in your browser settings.'
            })
          );
          permissionError.code = 'PERMISSION_DENIED';
          onError(permissionError);
        }
      }
    };

    startCamera();

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, [facingMode, onError, t]);

  const handleCapture = useCallback(() => {
    if (!videoRef.current || isCapturing) return;

    try {
      setIsCapturing(true);

      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!canvas) return;

      canvas.width = video.videoWidth || 1280;
      canvas.height = video.videoHeight || 720;
      const ctx = canvas.getContext('2d');

      if (mirrored) {
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
      }

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);

      const imageData = createImageData({
        dataUrl,
        name: `capture-${Date.now()}.jpg`,
        type: 'image/jpeg',
        size: Math.round(dataUrl.length * 0.75)
      });

      if (onCapture) {
        onCapture(imageData);
      }
    } catch (error) {
      console.error('Capture error:', error);
      if (onError) {
        onError(error);
      }
    } finally {
      setIsCapturing(false);
    }
  }, [isCapturing, mirrored, onCapture, onError]);

  return (
    <div className={`verification-webcam-container ${className}`}>
      <div className="relative h-full rounded-lg overflow-hidden bg-gray-900">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
          style={mirrored ? { transform: 'scaleX(-1)' } : undefined}
        />
        <canvas ref={canvasRef} className="hidden" />

        {hasPermission === false && (
          <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center">
            <div className="text-center p-4">
              <svg
                className="w-16 h-16 text-red-400 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <p className="text-white text-sm mb-2">
                {t('verification.camera-permission-required', {
                  defaultValue:
                    'Se requiere acceso a la cámara para la verificación'
                })}
              </p>
              <p className="text-white text-xs opacity-80">
                {t('verification.camera-permission-instructions', {
                  defaultValue:
                    'Por favor habilita los permisos de cámara en la configuración de tu navegador'
                })}
              </p>
            </div>
          </div>
        )}

        {hasPermission === null && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="text-center p-4">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-white border-t-transparent mx-auto mb-4"></div>
              <p className="text-white text-sm">
                {t('verification.camera-loading', {
                  defaultValue: 'Iniciando cámara...'
                })}
              </p>
            </div>
          </div>
        )}

        {/* Capture button - overlaid on video for mobile visibility */}
        {hasPermission === true && (
          <div className="absolute bottom-3 left-0 right-0 flex justify-center">
            <button
              type="button"
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-full shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              onClick={handleCapture}
              disabled={isCapturing}
            >
              {isCapturing ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  {t('verification.capturing', {
                    defaultValue: 'Capturando...'
                  })}
                </>
              ) : (
                <>
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  {t('verification.capture-button', {
                    defaultValue: 'Capturar foto'
                  })}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default WebcamCapture;